import React from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import {
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogHeader,
} from '@/components/common/CustomDialog';
import ControlledAssigneeSelect from '@/components/common/form/ControlledAssigneeSelect';
import ControlledMinMaxDonation from '@/components/common/form/ControlledMinMaxDonation';
import ControlledRadioGroup from '@/components/common/form/ControlledRadioGroup';
import ControlledSelect from '@/components/common/form/ControlledSelect';
import ControlledTextField from '@/components/common/form/ControlledTextField';
import LabelSelect from '@/components/common/form/LabelSelect';
import translate from '@/lib/intl/translate';
import AutomationType from '../constants/AutomationType';
import FormFields from '../constants/FormFields';
import LabelTarget from '../constants/LabelTarget';
import TaskDueDate from '../constants/TaskDueDate';
import TemporaryFormFields from '../constants/TemporaryFormFields';
import useAutomationsForm from '../hooks/useAutomationsForm';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { useAutomationsContext } from './AutomationsProvider';

const TYPE_OPTIONS = [
  { label: translate('AUTOMATIONS_CREATE_TASK'), value: AutomationType.TASK },
  { label: translate('AUTOMATIONS_APPLY_LABEL'), value: AutomationType.LABEL },
];

const DUE_DATE_OPTIONS = [
  { label: translate('AUTOMATIONS_DUE_DAY'), value: TaskDueDate.DAY },
  { label: translate('AUTOMATIONS_DUE_WEEK'), value: TaskDueDate.WEEK },
  { label: translate('AUTOMATIONS_DUE_MONTH'), value: TaskDueDate.MONTH },
];

const TARGET_OPTIONS = [
  {
    label: translate('AUTOMATIONS_LABEL_CONTRIBUTION'),
    value: LabelTarget.DONATION,
  },
  {
    label: translate('AUTOMATIONS_LABEL_CONSTITUENT'),
    value: LabelTarget.CONSTITUENT,
  },
];

function AutomationsForm() {
  const { closeForm, editingAutomation, isFormActive } =
    useAutomationsContext();
  const { formMethods, onSubmit } = useAutomationsForm(editingAutomation);
  const { control, formState, reset, watch } = formMethods;
  const watchType = watch('type');

  const handleClose = React.useCallback(() => {
    closeForm();
    reset();
  }, [closeForm]);

  React.useEffect(() => {
    if (formState.isSubmitSuccessful) {
      handleClose();
    }
  }, [formState]);

  return (
    <Dialog onClose={handleClose} open={isFormActive}>
      <FormProvider {...formMethods}>
        <form onSubmit={onSubmit}>
          <DialogHeader
            headerText={translate('AUTOMATIONS_CREATE_NEW')}
            onClose={handleClose}
          />
          <DialogContent dividers>
            <div className="flex flex-col gap-6">
              <ControlledMinMaxDonation
                label={translate('AUTOMATIONS_CONTRIBUTION_AMOUNT')}
                minRequired
              />
              <ControlledRadioGroup
                className="w-full md:grid md:grid-cols-2"
                name={FormFields.TYPE}
                options={TYPE_OPTIONS}
                control={control}
              />
              {watchType === AutomationType.TASK && (
                <>
                  <h3 className="type-body2">
                    {translate('AUTOMATIONS_TASK_HEADER')}
                  </h3>
                  <ControlledTextField
                    label={translate('TITLE_LABEL')}
                    name={FormFields.TASK_TITLE}
                    required
                    shouldUnregister
                  />
                  <ControlledTextField
                    label={translate('TASKS_NOTES')}
                    name={FormFields.TASK_NOTES}
                    shouldUnregister
                  />
                  <ControlledAssigneeSelect
                    name={TemporaryFormFields.ASSIGNEE}
                    shouldUnregister
                  />
                  <FormControl>
                    <InputLabel>{translate('TASKS_DUE_DATE')}</InputLabel>
                    <ControlledSelect
                      name={FormFields.TASK_DUE_DATE}
                      label={translate('TASKS_DUE_DATE')}
                      shouldUnregister
                    >
                      <MenuItem value="">{translate('NONE')}</MenuItem>
                      {DUE_DATE_OPTIONS.map(({ label, value }) => (
                        <MenuItem value={value} key={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </ControlledSelect>
                  </FormControl>
                </>
              )}
              {watchType === AutomationType.LABEL && (
                <>
                  <ControlledRadioGroup
                    className="w-full md:grid md:grid-cols-2"
                    defaultValue={LabelTarget.DONATION}
                    name={FormFields.LABEL_TARGET}
                    options={TARGET_OPTIONS}
                    control={control}
                    shouldUnregister
                  />
                  <h3 className="type-body2">
                    {translate('AUTOMATIONS_LABEL_HEADER')}
                  </h3>
                  <Controller
                    control={control}
                    name={TemporaryFormFields.LABELS}
                    render={({ field: { onChange, value } }) => (
                      <LabelSelect
                        onChange={onChange}
                        error={!!formState.errors?.[TemporaryFormFields.LABELS]}
                        helperText={
                          formState.errors?.[TemporaryFormFields.LABELS]
                            ?.message || ''
                        }
                        freeSolo
                        value={value}
                        required
                      />
                    )}
                    rules={{
                      validate: {
                        required: (v) => !!v?.length || translate('REQUIRED'),
                      },
                    }}
                    shouldUnregister
                  />
                </>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <DialogActionButton
              showLoader={formState.isSubmitting}
              type="submit"
            >
              {translate('SAVE')}
            </DialogActionButton>
            <DialogCancelButton onClick={handleClose}>
              {translate('CANCEL')}
            </DialogCancelButton>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}

export default AutomationsForm;
